import {
    Autocomplete,
    Box,
    Button,
    Collapse,
    FormControlLabel,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {
    Contact,
    NotificationChannel,
    NotificationRecipient
} from "../../models";
import {
    Dispatch,
    Fragment,
    SetStateAction,
    useCallback,
    useMemo,
    useState
} from "react";

import { NotificationRecipientIdType } from "../../API";
import React from "react";
import { RecipientNotificationSetting } from "./RecipientNotificationSetting";
import { ResourceNotificationSettingDraft } from "../../lib/dashboard/types/ResourceNotificationSettingDraft";

type AssessmentScheduleNotificationFormProps = {
    authorizedContactIds: Array<string>;
    contacts: Array<Contact>;
    setIsOpenCreateContactDialog: Dispatch<SetStateAction<boolean>>;
    notificationSetting: ResourceNotificationSettingDraft;
    setNotificationSetting: Dispatch<SetStateAction<ResourceNotificationSettingDraft>>;
};

export const AssessmentScheduleNotificationForm = (props: AssessmentScheduleNotificationFormProps) => {
    const {
        authorizedContactIds,
        contacts,
        setIsOpenCreateContactDialog,
        notificationSetting,
        setNotificationSetting
    } = props;

    const [searchValue, setSearchValue] = useState<string>("");

    const authorizedUserRecipients = useMemo<Array<NotificationRecipient>>(() => {
        return authorizedContactIds.map(contactId => {
            const existingRecipient = notificationSetting.recipients?.find(recipient => recipient.recipientId === contactId);
            if (!existingRecipient) {
                return {
                    recipientId: contactId,
                    recipientIdType: NotificationRecipientIdType.CONTACT,
                    enabled: false
                } satisfies NotificationRecipient;
            }
            return existingRecipient;
        });
    }, [authorizedContactIds, notificationSetting.recipients]);

    const additionalRecipients = useMemo<Array<NotificationRecipient>>(() => {
        return (notificationSetting.recipients ?? []).filter(recipient => !authorizedContactIds.includes(recipient?.recipientId ?? ""));
    }, [authorizedContactIds, notificationSetting.recipients]);

    const additionalContactOptions = useMemo(() => {
        return contacts
            .filter(contact => !notificationSetting.recipients?.some(recipient => recipient.recipientId === contact.id))
            .sort((a, b) => {
                if (a.name && b.name) {
                    return a.name.localeCompare(b.name);
                }
                return 0;
            });
    }, [contacts, notificationSetting.recipients]);

    const isOptionDisabled = useCallback((option: Contact) => {
        return !option.phoneNumber && !option.email;
    }, []);

    const removeAdditionalRecipient = useCallback((recipientId: string) => {
        setNotificationSetting(prevSetting => {
            return {
                ...prevSetting,
                recipients: prevSetting.recipients?.filter(recipient => recipient.recipientId !== recipientId)
            };
        });
    }, []);

    const toggleNotificationChannel = useCallback((recipientId: string, channel: NotificationChannel) => {
        setNotificationSetting(prevSetting => {
            const existingRecipient = prevSetting.recipients?.find(recipient => recipient.recipientId === recipientId);
            if (!existingRecipient) {
                return {
                    ...prevSetting,
                    recipients: [
                        ...(prevSetting.recipients ?? []),
                        {
                            recipientId,
                            recipientIdType: NotificationRecipientIdType.CONTACT,
                            notificationChannels: [channel],
                            enabled: true
                        }
                    ]
                };
            }
            return {
                ...prevSetting,
                recipients: prevSetting.recipients?.map(prevRecipient => {
                    if (prevRecipient.recipientId !== recipientId) {
                        return prevRecipient;
                    }
                    const newNotificationChannels = prevRecipient.notificationChannels?.includes(channel) ?
                        prevRecipient.notificationChannels?.filter(c => c !== channel) :
                        [...(prevRecipient.notificationChannels ?? []), channel];
                    return {
                        ...prevRecipient,
                        notificationChannels: newNotificationChannels,
                        enabled: newNotificationChannels.length > 0
                    };
                })
            };
        });
    }, []);

    return (
        <Fragment>
            <Box display="flex" gap={0.5} flexDirection="column">
                <Typography variant="body2" color="textSecondary">
                    Missed Assessment Notifications:
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Configure the contacts that will receive notifications when an assessment is missed.
                </Typography>
            </Box>
            <FormControlLabel
                value={!!notificationSetting?.enabled}
                control={
                    <Switch
                        checked={!!notificationSetting?.enabled}
                        onChange={(event) => setNotificationSetting(prevSetting => {
                            return { ...prevSetting, enabled: event.target.checked };
                        })}
                        name="enableNotifications"
                        color="success"
                        size="small"
                    />
                }
                label="Enable Notifications"
                color="success"
            />
            <Collapse in={!!notificationSetting?.enabled}>
                <Box display="flex" flexDirection="column" gap={2}>
                    {authorizedUserRecipients.length > 0 && (
                        <Box display="flex" flexDirection="column">
                            <Typography fontWeight={500}>
                                Authorized Users:
                            </Typography>
                            {authorizedUserRecipients.map((recipient, index) => {
                                const contact = contacts.find(contact => contact.id === recipient.recipientId);
                                if (!contact) {
                                    return null;
                                }
                                return (
                                    <RecipientNotificationSetting
                                        key={index}
                                        index={index}
                                        recipient={recipient}
                                        disableEmail={!contact.email}
                                        disableSMS={!contact.phoneNumber}
                                        recipientName={contact.name ?? "Unknown User"}
                                        toggleNotificationChannel={toggleNotificationChannel}
                                    />
                                );
                            }
                            )}
                        </Box>
                    )}
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                            <Typography fontWeight={500}>
                                Additional Contacts:
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => setIsOpenCreateContactDialog(true)}
                                size="small"
                                sx={{ textTransform: "none" }}
                                color="inherit"
                            >
                                Create New Contact
                            </Button>
                        </Box>
                        <Autocomplete
                            size="small"
                            fullWidth
                            placeholder="Select contact"
                            options={additionalContactOptions}
                            getOptionLabel={(option) => option.name || option.id}
                            onChange={(event, newValue, reason) => {
                                if (reason === "selectOption") {
                                    setSearchValue("");
                                    setNotificationSetting(prevSetting => {
                                        return {
                                            ...prevSetting,
                                            recipients: [
                                                ...(prevSetting.recipients ?? []),
                                                {
                                                    recipientId: newValue?.id,
                                                    recipientIdType: NotificationRecipientIdType.CONTACT,
                                                }
                                            ]
                                        };
                                    });
                                }
                            }}
                            value={null}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    placeholder="Search contact"
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.name} {isOptionDisabled(option) && "(No contact information)"}
                                </li>
                            )}
                            getOptionDisabled={isOptionDisabled}
                            inputValue={searchValue}
                            sx={{ mt: 1 }}
                        />
                        {additionalRecipients.length > 0 && (
                            additionalRecipients.map((recipient, index) => {
                                const contact = contacts.find(contact => contact.id === recipient.recipientId);
                                if (!contact) {
                                    return null;
                                }
                                return (
                                    <RecipientNotificationSetting
                                        key={index}
                                        index={index}
                                        recipient={recipient}
                                        disableEmail={!contact.email}
                                        disableSMS={!contact.phoneNumber}
                                        recipientName={contact.name ?? "Unknown User"}
                                        toggleNotificationChannel={toggleNotificationChannel}
                                        removeRecipient={removeAdditionalRecipient}
                                    />
                                );
                            })
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Fragment>
    );
};
